import type { ReactElement } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/AppShell";
import { IconChevron, IconPlus } from "../components/Icons";
import { PixBox } from "../components/PixBox";
import { EmptyState, Money, Skeleton } from "../components/ui";
import { firstName, formatCents } from "../lib/format";
import { useGroups, useSummary } from "../lib/queries";

export function HomePage(): ReactElement {
  const summary = useSummary();
  const groups = useGroups();

  const net = summary.data?.netCents ?? 0;

  return (
    <>
      <PageHeader title="Meus rolês" />

      <main className="app-content stack-lg">
        {/* O cartao que responde "e aí, eu devo ou me devem?" */}
        <section className="card-hero">
          <span style={{ opacity: 0.85, fontSize: "0.85rem", fontWeight: 600 }}>
            {net === 0 ? "Está tudo certo" : net > 0 ? "No saldo, você tem a receber" : "No saldo, você deve"}
          </span>
          <div className="summary-value">
            {summary.isLoading ? "—" : formatCents(Math.abs(net))}
          </div>

          <div className="summary-split">
            <div>
              <span style={{ fontSize: "0.8rem", opacity: 0.85 }}>Você deve</span>
              <div className="numeric" style={{ fontWeight: 700 }}>
                {formatCents(summary.data?.toPayCents ?? 0)}
              </div>
            </div>
            <div>
              <span style={{ fontSize: "0.8rem", opacity: 0.85 }}>Te devem</span>
              <div className="numeric" style={{ fontWeight: 700 }}>
                {formatCents(summary.data?.toReceiveCents ?? 0)}
              </div>
            </div>
          </div>
        </section>

        {/* Atalho de pagar: quem eu devo, com o Pix já na tela. */}
        {(summary.data?.topDebts.length ?? 0) > 0 ? (
          <section className="stack">
            <h2 className="section-title">Para quem você deve</h2>
            <div className="card">
              {summary.data?.topDebts.map((debt) => (
                <div key={debt.personId} className="transfer-card">
                  <div className="row-between">
                    <strong>{debt.displayName}</strong>
                    <span className="numeric negative" style={{ fontWeight: 700 }}>
                      {formatCents(debt.amountCents)}
                    </span>
                  </div>
                  <PixBox
                    pixKey={debt.pixKey}
                    pixKeyType={debt.pixKeyType}
                    ownerName={firstName(debt.displayName)}
                  />
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="stack">
          <div className="row-between">
            <h2 className="section-title">Rolês</h2>
            <Link className="btn btn-sm btn-subtle" to="/app/grupos/novo">
              <IconPlus width={16} height={16} />
              Novo
            </Link>
          </div>

          {groups.isLoading ? (
            <Skeleton height={78} count={3} />
          ) : groups.data && groups.data.length > 0 ? (
            <div className="stack">
              {groups.data.map((group) => (
                <Link key={group.id} className="group-card" to={`/app/grupos/${group.id}`}>
                  <span className="group-emoji" aria-hidden="true">
                    {group.emoji ?? "🧾"}
                  </span>
                  <span className="grow">
                    <span className="list-item-title">{group.name}</span>
                    <span className="muted" style={{ display: "block" }}>
                      {group.memberCount} {group.memberCount === 1 ? "pessoa" : "pessoas"} ·{" "}
                      {group.expenseCount} {group.expenseCount === 1 ? "despesa" : "despesas"}
                      {group.status === "SETTLED" ? " · quitado" : ""}
                    </span>
                  </span>
                  <span className="row" style={{ gap: "var(--space-1)" }}>
                    <Money cents={group.myBalanceCents} signed />
                    <IconChevron width={16} height={16} style={{ color: "var(--ink-faint)" }} />
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              emoji="🍻"
              title="Nenhum rolê ainda"
              description="Crie o primeiro e comece a lançar as despesas."
              action={
                <Link className="btn btn-primary" to="/app/grupos/novo">
                  Criar rolê
                </Link>
              }
            />
          )}
        </section>
      </main>

      <Link className="fab" to="/app/grupos/novo" aria-label="Criar novo rolê">
        <IconPlus />
        Novo rolê
      </Link>
    </>
  );
}
